import { Alert, AlertProps, Backdrop, CircularProgress, Dialog, Snackbar } from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { MQTTBrokerContext } from "contexts/MQTTBrokerContext/MQTTBrokerContext";
import {
    IMQTTBrokerSettingWithoutId,
    IMQTTBrokerSettings,
} from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { EventReportMQTTEntity } from "mqtt/entities/EventReport/EventReportEntity";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { MQTTBrokerSettingsEntity } from "entities/MQTTBrokerSettingsEntity/MQTTBrokerSettingsEntity";
import { MQTTSettingEditor } from "./MQTTSettingEditor";
import { OnlineStatusContext } from "contexts/OnlineStatusContext/OnlineStatusContext";
import { HMI_PATHS } from "constants/constants";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CheckUserPasswordModal from "components/CheckUserPasswordModal/CheckUserPasswordModal.view";

export const MQTTSettingDialog = () => {
    const [openDialog, setOpenDialog] = useState(false);
    const [openedAt, setOpenedAt] = useState<number | null>(null);
    const { currentSetting, openMQTTSettingDialog, handleOpenMQTTSettingDialog } = useContext(MQTTBrokerContext);
    const [alert, setAlert] = useState<{ message?: string; severity?: AlertProps["severity"]; show?: boolean }>({
        message: undefined,
        severity: undefined,
    });
    const [loading, setLoading] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [pendingSaveData, setPendingSaveData] = useState<Omit<IMQTTBrokerSettings, "id"> | null>(null);
    const { t } = useTranslation();
    const {
        store: { machineCenter },
        dispatch: { GetData },
    } = useContext(HMICachedDataContext);
    const { insertMQTTBrokerSetting } = MQTTBrokerSettingsEntity.InsertMQTTBrokerSetting();
    const { updateMQTTBrokerSetting } = MQTTBrokerSettingsEntity.UpdateMQTTBrokerSetting();
    const { onlineStatus } = useContext(OnlineStatusContext);
    const location = useLocation();

    useEffect(() => {
        if (openMQTTSettingDialog) {
            setOpenDialog(true);
            setOpenedAt(Date.now());
        }

        const eventListenerHandler = (e: KeyboardEvent) => {
            if (
                e.ctrlKey &&
                e.key === "m" &&
                location.pathname.toLowerCase().includes(`/${HMI_PATHS.ROOT_PATH.toLowerCase()}/${HMI_PATHS.DASHBOARD}`)
            ) {
                setOpenDialog(true);
            }
        };
        window.addEventListener("keydown", eventListenerHandler);
        return () => {
            window.removeEventListener("keydown", eventListenerHandler);
        };
    }, [openMQTTSettingDialog, location.pathname]);

    const testMosquittoBroker = useCallback(
        async (setting: IMQTTBrokerSettingWithoutId) => {
            setLoading(true);
            try {
                await EventReportMQTTEntity.testMQTTBroker(setting);
                setAlert({
                    message: t("mqtt_settings.messages.broker_connected"),
                    severity: "success",
                    show: true,
                });
            } catch (error) {
                setAlert({
                    message: t("mqtt_settings.messages.error_connecting_broker"),
                    severity: "error",
                    show: true,
                });
            }
            setLoading(false);
        },
        [setAlert, setLoading, t],
    );

    const handleSubmit = useCallback(
        async (values: Omit<IMQTTBrokerSettings, "id">) => {
            if (!onlineStatus) {
                setAlert({
                    message: t("mqtt_settings.messages.settings_online_required"),
                    severity: "error",
                    show: true,
                });
                return;
            }
            if (!machineCenter) {
                setAlert({
                    message: t("mqtt_settings.messages.machine_center_info_error"),
                    severity: "error",
                    show: true,
                });
                return;
            }

            setPendingSaveData(values);
            setShowPasswordModal(true);
        },
        [onlineStatus, machineCenter, t],
    );

    const handlePasswordConfirmed = useCallback(async () => {
        if (!pendingSaveData || !machineCenter) {
            return;
        }

        const { mill_id } = machineCenter.production_line.mill;
        const { host, name, user, password, allow_anonymous } = pendingSaveData;
        setLoading(true);

        const isUpdating = currentSetting && currentSetting.id !== "DEFAULT_OPTION_ID";

        try {
            if (isUpdating) {
                await updateMQTTBrokerSetting({
                    variables: {
                        id: currentSetting.id,
                        setting: {
                            host,
                            name,
                            allow_anonymous,
                            user,
                            password,
                        },
                    },
                });
                setAlert({
                    message: t("mqtt_settings.messages.setting_updated_success"),
                    severity: "success",
                    show: true,
                });
            } else {
                const setting = {
                    mill_id,
                    host,
                    name,
                    allow_anonymous,
                    user,
                    password,
                };
                await insertMQTTBrokerSetting({
                    variables: {
                        setting,
                    },
                });
                setAlert({
                    message: t("mqtt_settings.messages.setting_saved_success"),
                    severity: "success",
                    show: true,
                });
            }
        } catch (error) {
            console.log(error);
            setAlert({
                message: isUpdating
                    ? t("mqtt_settings.messages.error_updating_setting")
                    : t("mqtt_settings.messages.error_saving_setting"),
                severity: "error",
                show: true,
            });
        }
        await GetData(machineCenter.machine_center_id);
        setLoading(false);
        setPendingSaveData(null);
    }, [
        pendingSaveData,
        machineCenter,
        currentSetting,
        updateMQTTBrokerSetting,
        insertMQTTBrokerSetting,
        GetData,
        setAlert,
        setLoading,
        t,
    ]);

    const handlePasswordModalClose = useCallback(() => {
        setShowPasswordModal(false);
        setPendingSaveData(null);
    }, []);

    return (
        <Dialog
            open={openDialog}
            fullWidth={true}
            maxWidth="md"
            onClose={(_, reason) => {
                if (reason === "backdropClick" && openedAt && Date.now() - openedAt < 400) {
                    return;
                }
                setOpenDialog(false);
                handleOpenMQTTSettingDialog(false);
            }}
        >
            <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Snackbar
                open={Boolean(alert.show)}
                onClose={() => setAlert((alert) => ({ ...alert, show: false }))}
                autoHideDuration={6000}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert
                    severity={alert.severity}
                    onClose={() => setAlert((alert) => ({ ...alert, show: false }))}
                    sx={{ width: "100%", marginBottom: 6 }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
            <MQTTSettingEditor
                currentSetting={currentSetting?.id !== "DEFAULT_OPTION_ID" ? currentSetting : undefined}
                testMosquittoBroker={testMosquittoBroker}
                handleSave={handleSubmit}
                loading={loading}
            />
            <CheckUserPasswordModal
                open={showPasswordModal}
                onConfirmPassword={handlePasswordConfirmed}
                onClose={handlePasswordModalClose}
                title={t("mqtt_settings.password_modal.title")}
                question={t("mqtt_settings.password_modal.question")}
                questionConfirmedButtonText={t("mqtt_settings.password_modal.confirm_button")}
                variant="hmi"
            />
        </Dialog>
    );
};
